import { useState, useRef, useEffect, useCallback } from "react";
import { MoonStar } from "lucide-react";

const OPTIONS = [0, 5, 10, 15, 30];

export default function SleepTimer({ audioRef }) {
  const [minutes, setMinutes] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = (mins) => {
    clear();
    setMinutes(mins);
    const a = audioRef.current;
    if (!mins) {
      setRemaining(0);
      if (a) a.volume = 1;
      return;
    }
    const endAt = Date.now() + mins * 60000;
    setRemaining(mins * 60);
    intervalRef.current = setInterval(() => {
      const left = Math.max(0, Math.round((endAt - Date.now()) / 1000));
      setRemaining(left);
      const audio = audioRef.current;
      if (audio) {
        if (left <= 30 && left > 0) audio.volume = Math.max(0.03, left / 30);
        if (left === 0) {
          audio.pause();
          audio.volume = 1;
        }
      }
      if (left === 0) {
        clear();
        setMinutes(0);
      }
    }, 1000);
  };

  useEffect(() => () => clear(), [clear]);

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div data-testid="sleep-timer" className="mt-5 flex flex-wrap items-center justify-center gap-2">
      <span className="flex items-center gap-1.5 text-xs text-slate-400 mr-1">
        <MoonStar size={13} className="text-purple-400" /> Sleep timer
      </span>
      {OPTIONS.map((m) => (
        <button
          key={m}
          type="button"
          data-testid={`sleep-timer-${m}`}
          onClick={() => start(m)}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            minutes === m
              ? "bg-purple-600/40 border border-purple-400/50 text-purple-200"
              : "border border-purple-500/20 text-slate-500 hover:text-white"
          }`}
        >
          {m === 0 ? "Off" : `${m}m`}
        </button>
      ))}
      {minutes > 0 && (
        <span data-testid="sleep-timer-countdown" className="text-xs text-amber-300 font-mono ml-1">
          {fmt(remaining)}{remaining <= 30 ? " · fading out..." : ""}
        </span>
      )}
    </div>
  );
}
